<template>
  <base-dialog @cancel="cancelDialog">
    <div slot="header">{{ params.title }}</div>
    <div slot="body">
      <a-alert v-if="alertProps" v-bind="alertProps" class="mb-2" />
      <dialog-selected-tips :count="params.data.length" :action="this.params.title" :name="this.params.name" :unit="params.unit" />
      <dialog-table v-if="params.columns && params.columns.length" :data="params.data" :columns="params.columns.slice(0, 3)" />
      <dialog-content :content="params.content" />
    </div>
    <div slot="footer">
      <a-popconfirm v-if="params.showConfirm && params.confirmText" placement="topRight" :okText="$t('dialog.ok')" :cancelText="$t('dialog.cancel')" @confirm="handleConfirm">
        <template slot="title">
          <p>{{ params.confirmText }}</p>
        </template>
        <a-button type="danger">{{ submitButtonText }}</a-button>
      </a-popconfirm>
      <a-button v-else v-bind="okButtonProps" @click="handleConfirm" :loading="loading">{{ submitButtonText }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import * as R from 'ramda'
import { mapGetters } from 'vuex'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'
import workflowMixin from '@/mixins/workflow'
import { WORKFLOW_TYPES } from '@/constants/workflow'
import { uuid } from '@/utils/utils'

export default {
  name: 'DeleteRolePoliciesDialog',
  components: {
    DialogContent: {
      props: {
        content: {
          type: Function,
        },
      },
      render () {
        if (this.content) {
          return this.content()
        }
        return null
      },
    },
  },
  mixins: [DialogMixin, WindowsMixin, workflowMixin],
  data () {
    return {
      loading: false,
    }
  },
  computed: {
    ...mapGetters(['userInfo']),
    alertProps () {
      const { alert } = this.params
      const data = {
        String: { message: alert, type: 'warning' },
        Object: alert,
      }
      const t = R.type(alert)
      return data[t] || null
    },
    okButtonProps () {
      const defaultProps = {
        type: 'primary',
        loading: this.loading,
      }
      const { okButtonProps } = this.params
      if (okButtonProps && R.type(okButtonProps) === 'Object') {
        return Object.assign(defaultProps, okButtonProps)
      }
      return defaultProps
    },
    idKey () {
      return this.params.idKey || 'id'
    },
    isExecuteResourceOrderSetEnabled () {
      return this.checkWorkflowEnabled(WORKFLOW_TYPES.EXECUTE_ROLEPOLICY_BATCH_DELETE)
    },
    submitButtonText () {
      return this.isExecuteResourceOrderSetEnabled ? this.$t('system.text_532') : this.$t('dialog.ok')
    },
  },
  methods: {
    async handleConfirm () {
      this.loading = true
      try {
        const ids = this.params.data.map(item => item[this.idKey])
        // 如果启用了 execute-resource-order-set 工单类型，则创建工单
        if (this.isExecuteResourceOrderSetEnabled) {
          await this.deletePoliciesByWorkflow(ids, this.params.data)
        } else {
          // 否则直接删除
          if (this.params.ok) {
            await this.params.ok(ids, this.params.data)
          } else {
            let params = {}
            params = {
              ...params,
              ...this.params.requestParams,
            }
            const response = await this.params.onManager('batchDelete', {
              id: ids,
              managerArgs: { params, data: this.params.requestData },
            })
            if (this.params.vm && this.params.vm.destroySidePages) {
              this.params.vm.destroySidePage(this.params.vm.windowId)
            }
            if (this.params.success && R.is(Function, this.params.success)) {
              this.params.success(response)
            }
          }
          this.cancelDialog()
        }
      } finally {
        this.loading = false
      }
    },
    async deletePoliciesByWorkflow (ids, data) {
      try {
        // 获取角色信息（优先从 params.roleData 获取，否则从 data 中获取）
        const roleData = this.params.roleData || {}
        // 获取项目和项目域信息
        const projectId = roleData.project_id || this.userInfo.projectId || ''
        const domainId = roleData.domain_id || this.userInfo.projectDomainId || 'default'
        const projectDomainName = roleData.project_domain || (domainId === 'default' ? 'Default' : domainId)

        // 创建 resource_order_sets
        const orderSetManager = new this.$Manager('resource_order_sets')
        const { displayname, name } = this.userInfo
        const orderSetData = {
          auto_execute: false,
          name: this.$t('iam.role_delete_policies_workflow_name', [displayname || name, this.$moment().format('YYYY-MM-DD'), uuid()]),
          parameters: [{
            action: 'batch_delete',
            resource: 'rolepolicies',
            batch_resource_ids: ids,
            project: projectId || '',
            project_domain: projectDomainName,
          }],
        }
        const orderSetRes = await orderSetManager.create({ data: orderSetData })

        // 创建工单
        const variables = {
          process_definition_key: WORKFLOW_TYPES.EXECUTE_ROLEPOLICY_BATCH_DELETE,
          initiator: this.userInfo.id,
          ids: orderSetRes.data.id,
          parameter: '{}',
          project: projectId || '',
          project_domain: domainId,
          execute_resource_order_set_type: 'rolepolicies_batch_delete',
        }
        await this.createWorkflow(variables)
        this.$message.success(this.$t('common.worflow_tip', [this.params.title]))
        this.cancelDialog()
        this.$router.push('/workflow')
      } catch (error) {
        throw error
      }
    },
  },
}
</script>
